// ── 역대 당첨번호 공용 타입 & 순수 검증/정규화 헬퍼 (Supabase 의존 없음) ──
// drawHistory.ts 의 순수 로직을 분리한 모듈.
// 단위 테스트에서 Supabase 클라이언트/네트워크에 의존하지 않고 import 할 수 있다.

export interface Draw {
  id: number;
  draw_date: string;
  numbers: number[];
  bonus: number;
  prize_1st: number | null;
  winners_1st: number | null;
}

// B-1: select('*') 금지 — 필요한 컬럼만 조회
export const DRAW_HISTORY_COLUMNS = 'id,draw_date,numbers,bonus,prize_1st,winners_1st';

// 페이지네이션 페이지 크기 (500 이하 권장)
export const DRAW_PAGE_SIZE = 500;

export interface DateParts {
  year: number;
  month: number;
  day: number;
}

/**
 * YYYY-MM-DD 날짜 문자열을 엄격히 검증한다.
 * - 정확한 형식(4자리 연도, 2자리 월, 2자리 일)만 허용
 * - 실제 존재하는 날짜만 허용(2026-02-30, 2026-13-01 등은 무효)
 * - Date 자동 보정(new Date('2026-02-30') → 3월 2일)이나 로컬 타임존 보정에 의존하지 않음
 */
export function parseDateOnly(dateStr: string): DateParts | null {
  if (typeof dateStr !== 'string') return null;
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) {
    return null;
  }
  return { year, month, day };
}

/**
 * 본 번호 불변식: 정확히 6개, 모두 정수 1~45, 서로 중복 없음.
 */
export function isValidDrawNumbers(numbers: unknown): numbers is number[] {
  if (!Array.isArray(numbers)) return false;
  if (numbers.length !== 6) return false;
  const seen = new Set<number>();
  for (const n of numbers) {
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || n > 45) return false;
    if (seen.has(n)) return false; // 본 번호 6개는 서로 중복 금지
    seen.add(n);
  }
  return true;
}

/**
 * 서버 행을 안전하게 Draw로 변환한다. 다음 불변식을 모두 만족하지 않으면 null(비정상 행)을
 * 반환하고, 호출부는 invalid 카운트로 집계한다.
 * - 본 번호: 정확히 6개, 정수 1~45, 서로 중복 없음
 * - bonus: 정수 1~45, 본 번호 6개에 포함되지 않음
 * - draw_date: 실제 존재하는 YYYY-MM-DD 날짜(빈 값/무효 날짜는 제외)
 * - prize_1st/winners_1st: 음수·NaN·Infinity 등 비정상 값은 null로 정규화(행 자체는 제외하지 않음)
 */
export function sanitizeDraw(row: Record<string, unknown>): Draw | null {
  const id = typeof row.id === 'number' ? row.id : Number(row.id);
  if (!Number.isInteger(id) || id < 1) return null;

  if (!isValidDrawNumbers(row.numbers)) return null;
  const numbers = (row.numbers as number[]).slice().sort((a, b) => a - b);

  const bonus = typeof row.bonus === 'number' ? row.bonus : Number(row.bonus);
  if (!Number.isInteger(bonus) || bonus < 1 || bonus > 45) return null;
  if (numbers.includes(bonus)) return null; // bonus는 본 번호에 포함되면 안 됨

  const drawDate = typeof row.draw_date === 'string' ? row.draw_date : String(row.draw_date ?? '');
  if (!parseDateOnly(drawDate)) return null;

  const prizeRaw = row.prize_1st;
  const prize = prizeRaw == null ? null : Number(prizeRaw);
  const prizeClean = prize != null && Number.isFinite(prize) && prize >= 0 ? prize : null;

  const winnersRaw = row.winners_1st;
  const winners = winnersRaw == null ? null : Number(winnersRaw);
  const winnersClean = winners != null && Number.isFinite(winners) && winners >= 0 ? winners : null;

  return {
    id,
    draw_date: drawDate,
    numbers,
    bonus,
    prize_1st: prizeClean,
    winners_1st: winnersClean,
  };
}

// 일반 오류 메시지(공개 UI 노출용). raw DB 오류 상세는 공개하지 않는다.
export const GENERIC_DRAW_ERROR = '당첨 이력을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.';